/**
 * React Query hooks for credential management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  attachCredential,
  createCredential,
  deleteCredential,
  detachCredential,
  listCredentials,
} from '@/api/credentials';
import type { CredentialCreateRequest } from '@/types/credential';

const CREDS_KEY = ['credentials'] as const;

export function useCredentials(enabled = true) {
  return useQuery({
    queryKey: CREDS_KEY,
    queryFn: listCredentials,
    enabled,
  });
}

export function useCreateCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CredentialCreateRequest) => createCredential(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDS_KEY });
    },
  });
}

export function useDeleteCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentialId: string) => deleteCredential(credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDS_KEY });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useAttachCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, credentialId }: { assetId: string; credentialId: string }) =>
      attachCredential(assetId, credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDetachCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, credentialId }: { assetId: string; credentialId: string }) =>
      detachCredential(assetId, credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}
